import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useTrainingAuth } from '../../context/TrainingAuthContext';
import TrainingNavbar from '../../components/TrainingNavbar';
import trainingAuthService from '../../services/trainingAuthService';

const TrainingEditProfile = () => {
  const navigate = useNavigate();
  const { trainingUser, updateTrainingUser } = useTrainingAuth();

  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (trainingUser) setFullName(trainingUser.full_name || '');
  }, [trainingUser]);

  const dashboardPath = ['training_coordinator', 'training_document_controller'].includes(trainingUser?.role)
    ? '/training/coordinator'
    : '/training/reviewer';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!fullName.trim()) { setError('Full name is required'); return; }
    if (fullName.trim().length < 3) { setError('Full name must be at least 3 characters'); return; }

    try {
      setLoading(true);
      setError('');
      setSuccess('');
      const response = await trainingAuthService.updateProfile(fullName.trim());
      if (response.message) {
        setSuccess(response.message);
        if (response.user) updateTrainingUser(response.user);
        setTimeout(() => navigate(dashboardPath), 2000);
      }
    } catch (err) {
      setError(err.error || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <TrainingNavbar />
      <Container className="mt-5" style={{ maxWidth: '600px' }}>
        <Card className="shadow">
          <Card.Header className="text-white" style={{ background: 'linear-gradient(135deg, #198754 0%, #146c43 100%)' }}>
            <h4 className="mb-0">
              <i className="fas fa-user-edit me-2"></i>Edit Profile
            </h4>
          </Card.Header>
          <Card.Body>
            {success && (
              <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                <i className="fas fa-check-circle me-2"></i>{success}
              </Alert>
            )}
            {error && (
              <Alert variant="danger" dismissible onClose={() => setError('')}>
                <i className="fas fa-exclamation-circle me-2"></i>{error}
              </Alert>
            )}

            <Form onSubmit={handleSubmit}>
              <div className="mb-4 p-3 bg-light rounded">
                <h6 className="text-muted mb-3">Current Information</h6>
                <div className="row">
                  <div className="col-md-6 mb-2">
                    <small className="text-muted">Employee Code:</small>
                    <p className="mb-0 fw-bold">{trainingUser?.emp_code}</p>
                  </div>
                  <div className="col-md-6 mb-2">
                    <small className="text-muted">Email:</small>
                    <p className="mb-0 fw-bold">{trainingUser?.email}</p>
                  </div>
                  <div className="col-md-6 mb-2">
                    <small className="text-muted">Role:</small>
                    <p className="mb-0 fw-bold text-capitalize">
                      {trainingUser?.role?.replace(/_/g, ' ')}
                    </p>
                  </div>
                  <div className="col-md-6 mb-2">
                    <small className="text-muted">Status:</small>
                    <p className="mb-0">
                      <span className={`badge ${trainingUser?.email_verified ? 'bg-success' : 'bg-warning'}`}>
                        {trainingUser?.email_verified ? 'Verified' : 'Not Verified'}
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              <Form.Group className="mb-4">
                <Form.Label>Full Name <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter your full name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  disabled={loading}
                  required
                  minLength={3}
                />
                <Form.Text className="text-muted">
                  This name will be displayed on your profile and certificates.
                </Form.Text>
              </Form.Group>

              <div className="d-flex gap-2">
                <Button variant="success" type="submit" disabled={loading} className="flex-grow-1">
                  {loading
                    ? <><Spinner as="span" animation="border" size="sm" className="me-2" />Updating...</>
                    : <><i className="fas fa-save me-2"></i>Update Profile</>}
                </Button>
                <Button variant="secondary" onClick={() => navigate(dashboardPath)} disabled={loading}>
                  <i className="fas fa-times me-2"></i>Cancel
                </Button>
              </div>
            </Form>

            <div className="mt-4 p-3 border border-info rounded bg-light">
              <small className="text-muted">
                <i className="fas fa-info-circle me-2 text-info"></i>
                <strong>Note:</strong> Employee Code, Email, and Role cannot be changed.
                If you need to update these fields, please contact the administrator.
              </small>
            </div>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default TrainingEditProfile;
